import { createContext, type ReactNode, useCallback, useContext, useState } from 'react';

export type ModalName =
	| 'security'
	| 'passkeys'
	| 'api-keys'
	| 'users'
	| 'profile'
	| 'settings'
	| 'enrichment'
	| 'reddit-import'
	| 'tag-admin'
	| null;

interface ModalManagerContextValue {
	currentModal: ModalName;
	openModal: (name: ModalName) => void;
	closeModal: () => void;
}

const ModalManagerContext = createContext<ModalManagerContextValue | undefined>(undefined);

/**
 * ModalManagerProvider - Global state for page modals
 *
 * Wraps the app to provide modal management functionality
 */
export function ModalManagerProvider({ children }: { children: ReactNode }) {
	const [currentModal, setCurrentModal] = useState<ModalName>(null);

	const openModal = useCallback((name: ModalName) => {
		setCurrentModal(name);
	}, []);

	const closeModal = useCallback(() => {
		setCurrentModal(null);
	}, []);

	return (
		<ModalManagerContext.Provider value={{ currentModal, openModal, closeModal }}>
			{children}
		</ModalManagerContext.Provider>
	);
}

/**
 * useModalManager - Hook to access modal management
 *
 * Usage:
 * ```tsx
 * const { currentModal, openModal, closeModal } = useModalManager();
 *
 * // Open a modal
 * openModal('security');
 *
 * // Close current modal
 * closeModal();
 *
 * // Check if modal is open
 * if (currentModal === 'security') { ... }
 * ```
 */
export function useModalManager(): ModalManagerContextValue {
	const context = useContext(ModalManagerContext);
	if (!context) {
		throw new Error('useModalManager must be used within ModalManagerProvider');
	}
	return context;
}
